import type { EventDataV2 } from '@/types/ai/event_v2'
import { isUserLogin } from '@eureka/utils/account'
import { FactoryPsHttpSSE, type PsHttpSSE } from '@patsnap-utils/sse'
import {
    PsHttpService,
    type PatSnapCommonResponseData,
    type PatSnapSuccessResponseData,
} from '@patsnap/tahiti-core/common/ps-http'
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify'
import type {
    ContinueJobRequest,
    GenerateJobResponse,
    JobRunRequest,
    JobStreamLoopRequest,
    JobStreamLoopResponse,
    JobStreamRequest,
} from './index'
import type {
    GenerateJobRequestV2,
    GetJobIdByShareIdResponse,
    GetJobOwnershipResponse,
    GetReportFilesResponse,
    GetReportImgRequest,
    GetReportImgResponse,
    JobInputResponseV2,
    ResolveReportFilesRequest,
    ResolveReportFilesResponse,
} from './agent-api-v2.type'

export interface AgentApiV2 {
    continueJob(data: ContinueJobRequest): Promise<boolean>
    getJobStream(data: JobStreamRequest): Promise<PsHttpSSE<EventDataV2>>
    getJobStreamLoop(data: JobStreamLoopRequest): Promise<JobStreamLoopResponse>
    chatJob(data: GenerateJobRequestV2): Promise<GenerateJobResponse>
    runJob(data: JobRunRequest): Promise<JobRunRequest>
    getJobInput(jobId: string): Promise<JobInputResponseV2>
    getJobIdByShareId(shareId: string): Promise<GetJobIdByShareIdResponse>
    getJobOwnership(jobId: string): Promise<GetJobOwnershipResponse>
    terminateJob(jobId: string): Promise<GenerateJobResponse>
    getReportImg(data: GetReportImgRequest): Promise<GetReportImgResponse>
    getReportFiles(jobId: string): Promise<GetReportFilesResponse>
    resolveReportFiles(data: ResolveReportFilesRequest): Promise<ResolveReportFilesResponse>
}

@injectable()
export class AgentApiImplV2 extends PsHttpService implements AgentApiV2 {
    @inject(FactoryPsHttpSSE)
    private _factorySsePsHttp!: FactoryPsHttpSSE

    get options() {
        return {
            // baseURL: 'http://192.168.17.107:8001',
            baseURL: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/ai-agent-api`,
            headers: {
                'X-API-Version': '1.0',
            },
        };
    }

    private get jobApiBaseUrl() {
        return `${import.meta.env.VITE_EUREKA_SERVICE_URI}/core-lca-agent-api`
    }

    private getJobApiUrl(path: string) {
        return `${this.jobApiBaseUrl}${path}`
    }

    continueJob(data: ContinueJobRequest) {
        const { job_id, agent_code, ...restData } = data
        return this.post<PatSnapCommonResponseData<boolean>>({
            url: this.getJobApiUrl(`/job/${job_id}/reply`),
            data: {
                ...restData,
                job_id,
                ...(agent_code ? { code: agent_code } : {}),
            },
        }).then(res => res as unknown as boolean)
    }

    async getJobStream(data: JobStreamRequest) {
        const isLogin = await isUserLogin()
        const base = isLogin ? '' : '/guest'
        return this._factorySsePsHttp<EventDataV2>()
            .init({
                url: this.getJobApiUrl(`${base}/job/${data.job_id}/events`),
                data: {
                    reply: data.reply ?? false,
                    last_event_id: data.last_event_id ?? 0,
                    delay: 0,
                },
                hasFinalMessage: () => true,
            })
            .setFetchHeaders({
                'X-API-Version': '1.0',
            })
    }

    getJobStreamLoop(data: JobStreamLoopRequest) {
        return this.post<PatSnapSuccessResponseData<JobStreamLoopResponse>>({
            url: this.getJobApiUrl('/job/poll-messages'),
            data: {
                job_id: data.job_id,
                reply: data.reply ?? false,
                last_event_id: data.last_event_id ?? 0,
            },
        }).then(res => res.data)
    }

    chatJob(data: GenerateJobRequestV2) {
        return this.post<PatSnapCommonResponseData<GenerateJobResponse>>({
            url: this.getJobApiUrl('/job/chat'),
            data,
        }).then(res => res as unknown as GenerateJobResponse)
    }

    runJob(data: JobRunRequest) {
        return this.post<PatSnapCommonResponseData<JobRunRequest>>({
            url: this.getJobApiUrl('/job/run'),
            data,
        }).then(res => res as unknown as JobRunRequest)
    }

    async getJobInput(jobId: string) {
        const isLogin = await isUserLogin()
        const base = isLogin ? '' : '/guest'
        return this.get<PatSnapCommonResponseData<JobInputResponseV2>>({
            url: this.getJobApiUrl(`${base}/job/${jobId}`),
        }).then(res => res as unknown as JobInputResponseV2)
    }

    async getJobIdByShareId(shareId: string) {
        const isLogin = await isUserLogin()
        const base = isLogin ? '' : '/guest'
        return this.get<PatSnapCommonResponseData<GetJobIdByShareIdResponse>>({
            url: this.getJobApiUrl(`${base}/share/${shareId}/job`),
        }).then(res => res.data as unknown as GetJobIdByShareIdResponse)
    }

    getJobOwnership(jobId: string) {
        return this.get<PatSnapSuccessResponseData<GetJobOwnershipResponse>>({
            url: this.getJobApiUrl(`/job/${jobId}/ownership`),
        }).then(res => res.data)
    }

    terminateJob(jobId: string) {
        return this.post<PatSnapCommonResponseData<GenerateJobResponse>>({
            url: this.getJobApiUrl(`/job/${jobId}/terminate`),
            data: {},
        }).then(res => res as unknown as GenerateJobResponse)
    }

    getReportImg(data: GetReportImgRequest) {
        return this.post<PatSnapCommonResponseData<GetReportImgResponse>>({
            url: this.getJobApiUrl('report-assets/resolve'),
            data,
        }).then(res => res as unknown as GetReportImgResponse)
    }

    /**
     * Which data-file kinds a job produced — the set of download entries to offer.
     *
     * Signs nothing, so it is safe to call on page load. The answer comes from what
     * the backend recorded producing, which is why it is the authority for
     * availability rather than the resolve call's `missingKinds`.
     */
    getReportFiles(jobId: string) {
        return this.get<PatSnapCommonResponseData<GetReportFilesResponse>>({
            url: this.getJobApiUrl(`/report-files/${jobId}`),
        }).then(res => res as unknown as GetReportFilesResponse)
    }

    /**
     * Exchange a job id for short-lived signed URLs of the report's data files.
     *
     * Deliberately sends only the job id: the server rebuilds the object keys, so
     * this cannot be asked to sign an arbitrary object. The returned URLs expire,
     * so resolve at click time rather than caching them.
     */
    resolveReportFiles(data: ResolveReportFilesRequest) {
        return this.post<PatSnapCommonResponseData<ResolveReportFilesResponse>>({
            url: this.getJobApiUrl('/report-files/resolve'),
            data,
        }).then(res => res as unknown as ResolveReportFilesResponse)
    }
}
